import { useCallback, useEffect, useState } from "react";
import Link from "next/link";
import LazyImage from "@/my_components/Common/LazyImage";
import { useTranslation } from "react-i18next";

import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination } from "swiper/modules";

import { getInstructors } from "@/services/courseService";
import { useInfiniteScrollSentinel } from "@/my_components/Common/useInfiniteScrollSentinel";
import { ROUTE } from "@/route/app_routes.js";

// Every instructor card on the platform (homepage, /courses, /instructors) links here --
// CourseRoom.js reads `instructor`/`instructorName` off the query string and filters the
// catalog to just that creator's courses (see CoursesRepositories::SiteWideCoursesFetcher's
// tutorId filter), `instructorName` is display-only (the filter itself is the tokenized id).
export const instructorCoursesHref = (instructor) =>
  `${ROUTE.course}?instructor=${encodeURIComponent(instructor.instructor_id)}&instructorName=${encodeURIComponent(instructor.name || "")}`;

export const InstructorCard = ({ instructor, t }) => (
  <Link href={instructorCoursesHref(instructor)} className="rbt-instructor-card">
    <div className="rbt-instructor-card__media">
      {instructor.dp ? (
        <LazyImage src={instructor.dp} alt={instructor.name} fill sizes="(max-width: 767px) 90vw, 25vw" />
      ) : (
        <span className="rbt-instructor-card__initials" aria-hidden="true">
          {(instructor.name || "?").trim().charAt(0).toUpperCase()}
        </span>
      )}
      <div className="rbt-instructor-card__stats">
        <span>
          <i className="feather-video"></i>
          {instructor.total_courses} {instructor.total_courses !== 1 ? t("instructors.coursesLabel", "courses") : t("instructors.courseLabel", "course")}
        </span>
        <span>
          <i className="feather-users"></i>
          {instructor.total_students} {t("instructors.studentsLabel", "students")}
        </span>
      </div>
    </div>

    <div className="rbt-instructor-card__body">
      <h3 className="rbt-instructor-card__name">{instructor.name}</h3>
      <span className="rbt-instructor-card__role">{instructor.profession}</span>
      <p className="rbt-instructor-card__bio">{instructor.bio}</p>
    </div>
  </Link>
);

/**
 * layout="grid" (default): the original static 4-column grid, used on the Home and About pages.
 * layout="carousel": a Swiper carousel, 5-per-view on desktop, paging 5-at-a-time via the
 * next/prev arrows -- used on the homepage and the /courses page, both capped with `limit` so
 * neither fetches the full instructor roster (real YouTube creators can number in the hundreds).
 *
 * Two data-loading modes, picked by whether `paginated` is set:
 *  - default (flat): one fetch, optionally capped with `limit` (e.g. the homepage's `?limit=15`)
 *    and optionally `random` (a fresh shuffled slice each load, e.g. the /courses page carousel,
 *    vs. the homepage's deterministic top-by-credits ranking). Unchanged behaviour from before
 *    `limit`/`paginated` existed when neither prop is passed.
 *  - paginated: backed by the endpoint's `page`/`per_page` pagination, with an infinite-scroll
 *    "load more" grid (mirrors my_components/_Courses/CourseRoom.js's own course-list paging) --
 *    used by the dedicated /instructors page, which needs to show every tutor on the platform.
 */
const InstructorsShowcase = ({ layout = "grid", limit, random = false, paginated = false, perPage = 12, search = "" }) => {
  const { t } = useTranslation("home");
  const [instructors, setInstructors] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  // A new search term always restarts from page 1 -- otherwise "load more" would keep appending
  // pages computed against the OLD search term for a beat before the effect below catches up.
  useEffect(() => {
    if (paginated) setPage(1);
  }, [paginated, search]);

  // Flat, single-shot fetch -- homepage ("?limit=15"), About page, /courses carousel
  // ("?limit=15&random=1").
  useEffect(() => {
    if (paginated) return;
    setLoading(true);
    const params = {};
    if (limit) params.limit = limit;
    if (random) params.random = 1;
    getInstructors(params)
      .then((res) => setInstructors(res.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [paginated, limit, random]);

  // Paginated, load-more fetch -- the dedicated /instructors page.
  useEffect(() => {
    if (!paginated) return;
    const isFirstPage = page === 1;
    if (isFirstPage) setLoading(true);
    else setLoadingMore(true);

    const params = { page, per_page: perPage };
    if (search) params.search = search;
    getInstructors(params)
      .then((res) => {
        const pageData = res.data?.data || [];
        setInstructors((prev) => (isFirstPage ? pageData : [...prev, ...pageData]));
        setTotalPages(res.data?.last_page || 1);
      })
      .catch(() => {})
      .finally(() => {
        setLoading(false);
        setLoadingMore(false);
      });
  }, [paginated, page, perPage, search]);

  const canLoadMore = paginated && !loading && !loadingMore && page < totalPages;
  const loadMore = useCallback(() => setPage((p) => p + 1), []);
  const sentinelRef = useInfiniteScrollSentinel(loadMore, canLoadMore);

  if (loading) {
    return (
      <div className="rbt-instructor-grid">
        {Array.from({ length: 4 }).map((_, i) => (
          <div className="rbt-instructor-card" key={i}>
            <div className="rbt-skel" style={{ aspectRatio: "4/5", borderRadius: 20, marginBottom: 18 }} />
            <div className="rbt-skel rbt-course-card__skel-line rbt-course-card__skel-line--w60" style={{ height: 16 }} />
            <div className="rbt-skel rbt-course-card__skel-line rbt-course-card__skel-line--w90" style={{ height: 40 }} />
          </div>
        ))}
      </div>
    );
  }

  if (instructors.length === 0) {
    return paginated ? (
      <div className="rbt-course-empty">
        <i className="feather-users"></i>
        <h3>No instructors to show yet</h3>
        <p>Check back soon -- new creators are credited as fresh lessons go live.</p>
      </div>
    ) : null;
  }

  if (layout === "carousel") {
    return (
      <Swiper
        className="rbt-instructor-carousel event-activation-1 rbt-arrow-between rbt-dot-bottom-center pb--60"
        slidesPerView={1}
        slidesPerGroup={1}
        spaceBetween={24}
        modules={[Navigation, Pagination]}
        pagination={{ el: ".rbt-instructor-carousel-pagination", clickable: true }}
        navigation={{ nextEl: ".rbt-instructor-arrow-right", prevEl: ".rbt-instructor-arrow-left" }}
        breakpoints={{
          481: { slidesPerView: 2, slidesPerGroup: 2 },
          768: { slidesPerView: 3, slidesPerGroup: 3 },
          992: { slidesPerView: 4, slidesPerGroup: 4 },
          1200: { slidesPerView: 5, slidesPerGroup: 5 },
        }}
      >
        {instructors.map((instructor) => (
          <SwiperSlide key={instructor.instructor_id}>
            <InstructorCard instructor={instructor} t={t} />
          </SwiperSlide>
        ))}

        <div className="rbt-swiper-arrow rbt-arrow-left rbt-instructor-arrow-left">
          <div className="custom-overfolow">
            <i className="rbt-icon feather-arrow-left"></i>
            <i className="rbt-icon-top feather-arrow-left"></i>
          </div>
        </div>

        <div className="rbt-swiper-arrow rbt-arrow-right rbt-instructor-arrow-right">
          <div className="custom-overfolow">
            <i className="rbt-icon feather-arrow-right"></i>
            <i className="rbt-icon-top feather-arrow-right"></i>
          </div>
        </div>

        <div className="rbt-instructor-carousel-pagination rbt-swiper-pagination" style={{ bottom: "0" }}></div>
      </Swiper>
    );
  }

  return (
    <>
      <div className="rbt-instructor-grid">
        {instructors.map((instructor) => (
          <InstructorCard instructor={instructor} t={t} key={instructor.instructor_id} />
        ))}
      </div>

      {paginated && loadingMore && (
        <div className="rbt-instructor-grid mt--30">
          {Array.from({ length: 4 }).map((_, i) => (
            <div className="rbt-instructor-card" key={`more-${i}`}>
              <div className="rbt-skel" style={{ aspectRatio: "4/5", borderRadius: 20, marginBottom: 18 }} />
              <div className="rbt-skel rbt-course-card__skel-line rbt-course-card__skel-line--w60" style={{ height: 16 }} />
            </div>
          ))}
        </div>
      )}

      {paginated && page < totalPages && <div ref={sentinelRef} aria-hidden="true" />}

      {paginated && page >= totalPages && (
        <p className="rbt-infinite-end">
          <i className="feather-check-circle"></i> You&apos;ve reached the end -- that&apos;s every instructor on the platform.
        </p>
      )}
    </>
  );
};

export default InstructorsShowcase;
